import { getCroppedImg } from "../../utils/profileImage";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Cropper from "react-easy-crop";
import "./DimsumAvailabilityEditPage.css";
import progressIcon from "../../assets/progressIcon7.png";
import nextIcon from "../../assets/nextButtonIcon.png";
import nextIcon2 from "../../assets/nextButtonIcon2.png";
import morningIcon from "../../assets/availability1.png";
import afternoonIcon from "../../assets/availability2.png";
import eveningIcon from "../../assets/availability3.png";
import weekedaysIcon from "../../assets/availability4.png";
import weekenedsIcon from "../../assets/availability5.png";
import vacationsIcon from "../../assets/availability7.png";
import socialPawsLogo from "../../assets/socialPawsLogo.svg";
import { Plus, ArrowLeft, Loader2, X, ZoomIn, ZoomOut } from "lucide-react";
import apiClient from "../../api/apiClient";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import userPlaceholder from "../../assets/userPlaceholder.png";
import appIcon from "../../assets/appIcon.svg";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";



const DimsumAvailabilityEditPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useSelector((state) => state.user);

  // Mobile Step State (1 for Left Panel, 2 for Right Panel)
  const [mobileStep, setMobileStep] = useState(location.state?.step || 1);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const [formData, setFormData] = useState({
    dogName: "",
    availability: [],
    dogPhotos: { 0: null, 1: null, 2: null, 3: null },
  });

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropIndex, setCropIndex] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const imageRefs = useRef([]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${profile?.id}`);
        if (res.data?.status && res.data.data) {
          const userData = res.data.data;
          setUser(userData);

          if (userData.dogs && userData.dogs.length > 0) {
            const dog = userData.dogs[0];
            const dogPics = dog.dog_images || [];

            setFormData({
              dogName: dog.dog_name || "",
              availability: dog.availability || [],
              dogPhotos: {
                0: dogPics[0] || null,
                1: dogPics[1] || null,
                2: dogPics[2] || null,
                3: dogPics[3] || null,
              },
            });
          }
        }
      } catch (err) {
        console.error("Error fetching details:", err);
      } finally {
        setLoading(false);
      }
    };
    if (profile?.id) fetchDetails();
  }, [profile?.id]);

  const dogName = formData.dogName || "Dog";

  const handleImageSelect = (event, index) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setCropImageSrc(reader.result);
        setCropIndex(index);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCropModalOpen(true);
      };
      event.target.value = "";
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleSaveCrop = async () => {
    try {
      const croppedFile = await getCroppedImg(
        cropImageSrc,
        croppedAreaPixels,
        cropIndex,
      );
      if (croppedFile) {
        setFormData((prev) => ({
          ...prev,
          dogPhotos: { ...prev.dogPhotos, [cropIndex]: croppedFile },
        }));
      }
    } catch (e) {
      console.error("Error generating crop", e);
    } finally {
      setCropModalOpen(false);
      setCropImageSrc(null);
    }
  };

  const handleCancelCrop = () => {
    setCropModalOpen(false);
    setCropImageSrc(null);
  };

  const getPreviewUrl = (photo) => {
    if (!photo) return dogPlaceholder;
    if (typeof photo === "string") return photo;
    return URL.createObjectURL(photo);
  };

  const availabilityOptions = [
    { id: "mornings", label: "Mornings", icon: morningIcon, large: false },
    {
      id: "afternoons",
      label: "Afternoons",
      icon: afternoonIcon,
      large: false,
    },
    { id: "evenings", label: "Evenings", icon: eveningIcon, large: false },
    { id: "weekdays", label: "Weekdays", icon: weekedaysIcon, large: true },
    { id: "weekends", label: "Weekends", icon: weekenedsIcon, large: true },
    { id: "vacations", label: "Vacations", icon: vacationsIcon, large: false },
  ];

  const toggleAvailability = (id) => {
    setFormData((prev) => {
      const current = prev.availability || [];
      const updated = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      return { ...prev, availability: updated };
    });
  };

  const hasAvailability = formData.availability.length >= 1;
  const hasAtLeastOneImage = Object.values(formData.dogPhotos).some(
    (img) => img !== null,
  );

  // Validation checks
  const isStep1Valid = hasAvailability;
  const isStep2Valid = hasAtLeastOneImage;
  const isFinalNextEnabled =
    hasAvailability && hasAtLeastOneImage && !isUpdating;

  const handleNext = async () => {
    // Check constraints based on screen size
    if (window.innerWidth >= 1024) {
      if (!isFinalNextEnabled || isUpdating) return;
    } else {
      if (!isStep2Valid || isUpdating) return;
    }

    setIsUpdating(true);

    try {
      const dogId = user?.dogs?.[0]?.id;
      if (!dogId) return;

      // Prepare General Data (Availability)
      const generalData = new FormData();
      generalData.append("user_id", profile?.id);
      generalData.append("availability", JSON.stringify(formData.availability));

      // Create a promise for the general update API call
      const updateGeneralPromise = apiClient.put(
        `/dogs/update-dog-by-id/${dogId}`,
        generalData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      // Prepare Image Data (Only newly uploaded files)
      const imageData = new FormData();
      const updatedIndexes = [];
      let hasNewImages = false;

      [0, 1, 2, 3].forEach((index) => {
        const item = formData.dogPhotos[index];
        if (item instanceof File) {
          imageData.append("dog_images", item);
          updatedIndexes.push(index);
          hasNewImages = true;
        }
      });

      const promises = [updateGeneralPromise];

      // If there are new images, stringify the array and call the image update API
      if (hasNewImages) {
        imageData.append("indexes", JSON.stringify(updatedIndexes));

        const updateImagesPromise = apiClient.put(
          `/dogs/update-dog-images-by-index/${dogId}`,
          imageData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        promises.push(updateImagesPromise);
      }

      // Wait for all active updates to finish
      const responses = await Promise.all(promises);

      // Check if the primary response (general data) was successful
      const generalResponse = responses[0];
      if (generalResponse.data?.status) {
        navigate("/edit-exp-with-dogs");
      }
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleImgError = (e) => {
    e.target.src = dogPlaceholder;
  };

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background:
            "linear-gradient(157.14deg, #96f2de 2.76%, #58b8a3 96.76%)",
          zIndex: 9999,
        }}
      >
        <Loader2 className="animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div
      className={`dae-container ${mobileStep === 1 ? "dae-mobile-step-1" : "dae-mobile-step-2"}`}
    >
      {/* LEFT PANEL: Availability Selection (Mobile Step 1) */}
      <div
        className={`dae-panel dae-left-panel ${mobileStep === 1 ? "dae-mobile-active" : "dae-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={() => {
            if (window.innerWidth < 1024) {
              navigate("/edit-team-paws-personality", {
                state: { step: 2 },
              });
            } else {
              navigate(-1);
            }
          }}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
        />

        <DesktopTopBar
          showBack={true}
          onBack={() => navigate("/edit-team-paws-personality")}
          backColor="#000"
        />

        <div className="dae-content-wrapper">
          <div className="dae-header-section">
            <div className="dae-dog-icon-container">
              <img
                src={progressIcon}
                alt="Progress"
                className="dae-progress-icon"
              />
            </div>
            <div className="dae-progress-track">
              <div className="dae-progress-fill dae-step-1-fill"></div>
            </div>
          </div>

          <h2 className="dae-title">
            WHAT IS {dogName.toUpperCase()}'S AVAILABILITY?
          </h2>
          <p className="dae-subtitle">
            Generally, when can others request walks or playdates with {dogName}
            ? Can be pupdated anytime.
          </p>

          <div className="dae-avail-grid">
            {availabilityOptions.map((opt) => (
              <div
                key={opt.id}
                className={`dae-avail-card ${formData.availability.includes(opt.id) ? "selected" : ""}`}
                onClick={() => toggleAvailability(opt.id)}
              >
                <div className="dae-icon-wrapper">
                  <img
                    src={opt.icon}
                    alt={opt.label}
                    className={opt.large ? "dae-icon-lg" : "dae-icon-sm"}
                  />
                </div>
                <span className="dae-avail-label">{opt.label}</span>
              </div>
            ))}
          </div>

          <MobileBottomBar
            onNext={() => isStep1Valid && setMobileStep(2)}
            isNextEnabled={isStep1Valid}
            nextIconSrc={nextIcon2}
            showSkip={true}
            isSkipBlue={true}
            skipText="EXIT"
            onSkip={() => navigate("/user-profile")}
            variant="blue"
          />
        </div>
      </div>

      {/* RIGHT PANEL: Bio/Photos (Mobile Step 2) */}
      <div
        className={`dae-panel dae-right-panel ${mobileStep === 2 ? "dae-mobile-active" : "dae-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={() => setMobileStep(1)}
          backColor="#fff"
          logoColor="#fff"
        />

        <div className="dae-content-wrapper dae-right-content">
          {/* Mobile Only Progress (Mirrored) */}
          <div className="dae-header-section dae-mobile-only">
            <div className="dae-dog-icon-container">
              <img
                src={progressIcon}
                alt="Progress Step"
                className="dae-progress-icon"
              />
            </div>
            <div className="dae-progress-track">
              <div className="dae-progress-fill dae-step-2-fill"></div>
            </div>
          </div>

          <div className="dae-text-group">
            <h1 className="dae-title dae-text-white">
              {dogName.toUpperCase()}'S PICS
            </h1>
            <p className="dae-subtitle dae-text-white-sub">
              A picture is worth a thousand words! A tongue-out headshot, a
              picture with you, even a puppy pic are all great choices!
            </p>
          </div>

          <div className="dae-photo-grid">
            {/* Main Photo (Index 0) */}
            <div
              className="dae-photo-box dae-main-photo"
              onClick={() => imageRefs.current[0].click()}
            >
              <input
                type="file"
                hidden
                ref={(el) => (imageRefs.current[0] = el)}
                accept="image/*"
                onChange={(e) => handleImageSelect(e, 0)}
              />
              {formData.dogPhotos[0] ? (
                <img
                  src={getPreviewUrl(formData.dogPhotos[0])}
                  alt="Main"
                  className="dae-uploaded-img"
                  onError={handleImgError}
                />
              ) : (
                <Plus size={40} color="#000" />
              )}
            </div>

            {/* Sub Photos (Indices 1, 2 and 3) */}
            <div className="dae-sub-photos-row">
              {[1, 2, 3].map((index) => (
                <div
                  key={index}
                  className="dae-photo-box dae-sub-photo"
                  onClick={() => imageRefs.current[index].click()}
                >
                  <input
                    type="file"
                    hidden
                    ref={(el) => (imageRefs.current[index] = el)}
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, index)}
                  />
                  {formData.dogPhotos[index] ? (
                    <img
                      src={getPreviewUrl(formData.dogPhotos[index])}
                      alt={`Sub ${index}`}
                      className="dae-uploaded-img"
                      onError={handleImgError}
                    />
                  ) : (
                    <Plus size={32} color="#000" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Footer Navigation */}
          <div className="dae-footer-nav dae-desktop-only">
            <button
              className="dae-skip-btn"
              onClick={() => navigate("/edit-exp-with-dogs")}
            >
              SKIP
            </button>
            <button
              className="dae-skip-btn"
              onClick={() => navigate("/user-profile")}
            >
              EXIT
            </button>
            <button
              className={`dae-next-btn-group ${isFinalNextEnabled ? "enabled" : "disabled"}`}
              onClick={handleNext}
              disabled={isUpdating}
            >
              <span className="dae-next-text">
                {isUpdating ? "UPDATING..." : "NEXT"}
              </span>
              <div className="dae-next-circle">
                {isUpdating ? (
                  <Loader2 className="animate-spin" color="white" />
                ) : (
                  <img src={nextIcon} alt="Next" className="dae-next-img" />
                )}
              </div>
            </button>
          </div>

          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={isStep2Valid && !isUpdating}
            nextIconSrc={nextIcon}
            showSkip={true}
            skipText="EXIT"
            onSkip={() => navigate("/user-profile")}
            variant="green"
          />
        </div>
      </div>

      {/* Custom Cropper Modal */}
      {cropModalOpen && cropImageSrc && (
        <div className="crop-modal-overlay">
          <div className="crop-modal-box">
            <button className="crop-close-btn" onClick={handleCancelCrop}>
              <X size={20} color="rgba(166, 28, 31, 1)" strokeWidth={4} />
            </button>

            <h2 className="crop-modal-title">Upload Picture</h2>

            <div className="crop-container">
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                classes={{
                  containerClassName: "custom-cropper-container",
                }}
                cropAreaStyle={{
                  border: "2px dashed white",
                }}
              />
            </div>

            <div className="crop-slider-wrapper">
              <ZoomOut size={24} color="#000" />
              <input
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="Zoom"
                onChange={(e) => setZoom(e.target.value)}
                className="crop-slider"
              />
              <ZoomIn size={24} color="#000" />
            </div>

            <div className="crop-action-row">
              <button
                className="crop-btn-change"
                onClick={() => {
                  imageRefs.current[cropIndex].click();
                }}
              >
                CHANGE IMAGE
              </button>
              <button className="crop-btn-save" onClick={handleSaveCrop}>
                CROP & SAVE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DimsumAvailabilityEditPage;
